// ============================================================================
// Supply Management — Catalog
// Three sub-tabs: Supplies (227 items), Vendors (19 records), Pricing (vendor
// x supply matrix, ~192 records). Mobile-first: on phone widths the pricing
// matrix collapses to a per-item card view (item → vertical list of vendor
// prices, cheapest highlighted in green).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { ErrorState, PageHeader } from '../../shared/components';
import type {
  PreferredVendor,
  Supply,
  VendorSupplyPricing,
} from '../../shared/types';
import { SUPPLY_CATEGORY_LABELS, type SupplyCategoryCode } from '../../shared/types';
import { SupplySubNav } from './supply-sub-nav';
import { errorMessage } from '../../shared/errors';

type CatalogTab = 'supplies' | 'vendors' | 'pricing';

const TABS: { key: CatalogTab; label: string }[] = [
  { key: 'supplies', label: 'Supplies' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'pricing', label: 'Pricing' },
];

export default function CatalogPage() {
  const [tab, setTab] = useState<CatalogTab>('supplies');

  return (
    <div className="mx-auto max-w-6xl p-4 pb-28 sm:p-6">
      <SupplySubNav />

      <PageHeader title="Supply catalog" meta="Reference data — supplies, vendors, and pricing" />

      <div className="mt-3 flex gap-1 rounded-md border border-tcs-border bg-tcs-surface p-0.5 text-sm">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 rounded px-3 py-1.5 ${
              tab === t.key
                ? 'bg-tcs-inverse text-tcs-inverse-ink'
                : 'text-tcs-text-secondary hover:bg-tcs-bg'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === 'supplies' && <SuppliesTab />}
        {tab === 'vendors' && <VendorsTab />}
        {tab === 'pricing' && <PricingTab />}
      </div>
    </div>
  );
}

// ---------- Supplies tab ----------------------------------------------------

function SuppliesTab() {
  const [rows, setRows] = useState<Supply[]>([]);
  const [filter, setFilter] = useState('');
  const [cat, setCat] = useState<SupplyCategoryCode | 'ALL'>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('supplies')
        .select(
          'supply_id, item_name, sku, supply_category, sub_category, preferred_vendor, vendor_id, unit, unit_cost, reimbursable, is_capital_equipment',
        )
        .order('item_name');
      if (e) throw e;
      setRows((data ?? []) as unknown as Supply[]);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load supplies.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const lc = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (cat !== 'ALL' && r.supply_category !== cat) return false;
      if (!lc) return true;
      const hay = [r.item_name, r.sku, r.preferred_vendor, r.sub_category]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(lc);
    });
  }, [rows, filter, cat]);

  if (error) return <ErrorState body={error} onRetry={load} />;

  return (
    <>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name, SKU, or vendor…"
          className="flex-1 rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
        />
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value as SupplyCategoryCode | 'ALL')}
          className="rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
        >
          <option value="ALL">All categories</option>
          {(Object.keys(SUPPLY_CATEGORY_LABELS) as SupplyCategoryCode[]).map((k) => (
            <option key={k} value={k}>
              {SUPPLY_CATEGORY_LABELS[k]}
            </option>
          ))}
        </select>
      </div>
      <p className="mb-2 text-xs text-tcs-text-secondary">
        {loading ? 'Loading…' : `${filtered.length} of ${rows.length} supplies`}
      </p>
      <ul className="space-y-1">
        {filtered.map((s) => (
          <li
            key={s.supply_id}
            className="rounded-md border border-tcs-border bg-tcs-surface p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-tcs-text">
                  {s.item_name ?? s.supply_id}
                </div>
                <div className="mt-0.5 text-xs text-tcs-text-secondary">
                  <span className="font-mono">{s.supply_id}</span>
                  {s.sku ? ` · ${s.sku}` : ''}
                  {s.supply_category
                    ? ` · ${
                        SUPPLY_CATEGORY_LABELS[s.supply_category as SupplyCategoryCode] ??
                        s.supply_category
                      }`
                    : ''}
                  {s.preferred_vendor ? ` · ${s.preferred_vendor}` : ''}
                </div>
              </div>
              <div className="text-right text-sm">
                <div className="font-semibold text-tcs-text">
                  {s.unit_cost != null ? `$${Number(s.unit_cost).toFixed(2)}` : '—'}
                </div>
                <div className="text-xs text-tcs-text-secondary">{s.unit ?? ''}</div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

// ---------- Vendors tab ----------------------------------------------------

function VendorsTab() {
  const [rows, setRows] = useState<PreferredVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('preferred_vendors')
        .select(
          'vendor_id, vendor_name, primary_category, lead_time_days, same_day_capable, status, unit_price_avg, case_price_avg, delivery_fee',
        )
        .order('vendor_name');
      if (e) throw e;
      setRows((data ?? []) as unknown as PreferredVendor[]);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load vendors.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={load} />;

  return (
    <>
      <p className="mb-2 text-xs text-tcs-text-secondary">
        {loading ? 'Loading…' : `${rows.length} vendors`}
      </p>
      <ul className="space-y-2">
        {rows.map((v) => (
          <li
            key={v.vendor_id}
            className="rounded-md border border-tcs-border bg-tcs-surface p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-tcs-text">
                  {v.vendor_name ?? v.vendor_id}
                </div>
                <div className="mt-0.5 text-xs text-tcs-text-secondary">
                  <span className="font-mono">{v.vendor_id}</span>
                  {v.primary_category ? ` · ${v.primary_category}` : ''}
                  {v.lead_time_days ? ` · lead ${v.lead_time_days}` : ''}
                  {v.same_day_capable && v.same_day_capable.toLowerCase() === 'yes'
                    ? ' · same-day'
                    : ''}
                  {v.status ? ` · ${v.status}` : ''}
                </div>
              </div>
              <div className="text-right text-xs text-tcs-text-secondary">
                {v.delivery_fee != null && Number(v.delivery_fee) > 0
                  ? `Delivery $${Number(v.delivery_fee).toFixed(2)}`
                  : 'Free delivery'}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

// ---------- Pricing tab ----------------------------------------------------

function PricingTab() {
  const [rows, setRows] = useState<VendorSupplyPricing[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('vendor_supply_pricing')
        .select(
          'vsp_id, vendor_id, vendor_name, supply_id, item_name, vendor_sku, vendor_item_name, unit_price, case_price, uom, pack_size, lead_time_days, contract_price_yn, notes',
        )
        .order('item_name');
      if (e) throw e;
      setRows((data ?? []) as unknown as VendorSupplyPricing[]);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load pricing.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Group by supply, find cheapest unit_price across vendors for highlighting
  const grouped = useMemo(() => {
    const lc = filter.trim().toLowerCase();
    const map = new Map<string, VendorSupplyPricing[]>();
    for (const r of rows) {
      if (lc) {
        const hay = [r.item_name, r.vendor_name, r.vendor_sku]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(lc)) continue;
      }
      const key = r.supply_id ?? r.item_name ?? '?';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows, filter]);

  if (error) return <ErrorState body={error} onRetry={load} />;

  return (
    <>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by item or vendor…"
        className="mb-3 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
      />
      <p className="mb-2 text-xs text-tcs-text-secondary">
        {loading
          ? 'Loading…'
          : `${grouped.length} item${grouped.length === 1 ? '' : 's'} · ${rows.length} price record${rows.length === 1 ? '' : 's'}`}
      </p>
      <ul className="space-y-3">
        {grouped.map(([key, entries]) => {
          const cheapest = entries.reduce<number | null>((min, r) => {
            const v = r.unit_price;
            if (v == null) return min;
            return min == null || Number(v) < min ? Number(v) : min;
          }, null);
          const firstName = entries[0]?.item_name ?? key;
          return (
            <li
              key={key}
              className="rounded-md border border-tcs-border bg-tcs-surface p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-tcs-text">
                    {firstName}
                  </div>
                  <div className="font-mono text-xs text-tcs-text-secondary">
                    {key}
                  </div>
                </div>
                <div className="text-xs text-tcs-text-secondary">
                  {entries.length} vendor{entries.length === 1 ? '' : 's'}
                </div>
              </div>
              <ul className="space-y-1">
                {entries.map((e) => {
                  const isCheapest =
                    cheapest != null &&
                    e.unit_price != null &&
                    Number(e.unit_price) === cheapest;
                  return (
                    <li
                      key={e.vsp_id}
                      className={[
                        'flex items-center justify-between rounded px-2 py-1.5 text-sm',
                        isCheapest
                          ? 'bg-st-ok-bg ring-1 ring-st-ok-border'
                          : 'bg-tcs-raised',
                      ].join(' ')}
                    >
                      <span className="min-w-0 flex-1 truncate text-tcs-text">
                        {e.vendor_name ?? e.vendor_id ?? '—'}
                        {e.contract_price_yn?.toLowerCase() === 'yes' ? (
                          <span className="ml-1 text-xs text-st-ok-fg">★</span>
                        ) : null}
                      </span>
                      <span className="ml-2 text-right text-tcs-text">
                        {e.unit_price != null
                          ? `$${Number(e.unit_price).toFixed(2)}`
                          : '—'}
                        {e.case_price != null ? (
                          <span className="ml-2 text-xs text-tcs-text-secondary">
                            (case ${Number(e.case_price).toFixed(2)})
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </>
  );
}
